import * as THREE from 'three';
import type { BonusCardMotif, BonusCardPalette, BonusCardVariant } from './catalog';

export type BonusCardTexturePair = Readonly<{
  front: THREE.CanvasTexture;
  back: THREE.CanvasTexture;
}>;

export type BonusCardTextureOptions = Readonly<{
  width?: number;
  height?: number;
  anisotropy?: number;
}>;

const DEFAULT_TEXTURE_WIDTH = 1024;
const DEFAULT_TEXTURE_HEIGHT = 648;
const FONT_STACK = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export function createBonusCardTexturePair(
  variant: BonusCardVariant,
  options: BonusCardTextureOptions = {},
): BonusCardTexturePair {
  const width = options.width ?? DEFAULT_TEXTURE_WIDTH;
  const height = options.height ?? DEFAULT_TEXTURE_HEIGHT;
  if (width < 256 || height < 160) {
    throw new Error('Bonus card textures require at least a 256 by 160 canvas.');
  }

  const frontCanvas = createCanvas(width, height);
  const backCanvas = createCanvas(width, height);
  const frontContext = getContext(frontCanvas);
  const backContext = getContext(backCanvas);

  drawFrontArtwork(frontContext, width, height, variant);
  drawBackArtwork(backContext, width, height, variant);

  const front = configureTexture(new THREE.CanvasTexture(frontCanvas), options.anisotropy ?? 8);
  const back = configureTexture(new THREE.CanvasTexture(backCanvas), options.anisotropy ?? 8);
  front.name = `BonusCardTexture:Front:${variant.id}`;
  back.name = `BonusCardTexture:Back:${variant.id}`;
  front.userData = { assetType: 'bonus-card-texture', side: 'front', variantId: variant.id };
  back.userData = { assetType: 'bonus-card-texture', side: 'back', variantId: variant.id };

  return Object.freeze({ front, back });
}

function createCanvas(width: number, height: number): HTMLCanvasElement {
  if (typeof document === 'undefined') {
    throw new Error('Bonus card CanvasTextures can only be created in a browser document.');
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function getContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not create a 2D context for bonus card artwork.');
  return context;
}

function configureTexture(texture: THREE.CanvasTexture, anisotropy: number): THREE.CanvasTexture {
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = Math.max(1, Math.floor(anisotropy));
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}

function drawFrontArtwork(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  variant: BonusCardVariant,
): void {
  context.clearRect(0, 0, width, height);
  withRoundedClip(context, width, height, height * 0.105, () => {
    drawBase(context, width, height, variant.palette, false);
    drawMotif(context, width, height, variant.motif, variant.palette, variant.artSeed);
    drawPrintedFoil(context, width, height, variant.palette);
    drawInnerKeyline(context, width, height, variant.palette);

    const topPillWidth = width * 0.42;
    roundedRectPath(
      context,
      (width - topPillWidth) * 0.5,
      height * 0.075,
      topPillWidth,
      height * 0.115,
      height * 0.058,
    );
    context.fillStyle = rgba(variant.palette.deep, 0.82);
    context.fill();
    context.strokeStyle = rgba(variant.palette.paper, 0.4);
    context.lineWidth = Math.max(2, height * 0.005);
    context.stroke();

    drawFittedText(context, variant.headline, width * 0.5, height * 0.135, {
      maxWidth: topPillWidth * 0.82,
      maxSize: height * 0.064,
      minSize: height * 0.038,
      fill: variant.palette.paper,
      weight: 900,
      letterSpacing: height * 0.006,
    });

    drawSpark(context, width * 0.145, height * 0.235, height * 0.055, variant.palette.foil);
    drawSpark(context, width * 0.855, height * 0.235, height * 0.042, variant.palette.paper);

    const valueY = height * 0.48;
    const valueSize = variant.denomination >= 1000 ? height * 0.29 : height * 0.34;
    context.save();
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = `1000 ${valueSize}px ${FONT_STACK}`;
    context.lineJoin = 'round';
    context.shadowColor = rgba(variant.palette.deep, 0.72);
    context.shadowBlur = height * 0.025;
    context.shadowOffsetY = height * 0.018;
    context.strokeStyle = rgba(variant.palette.deep, 0.9);
    context.lineWidth = height * 0.028;
    context.strokeText(variant.denominationLabel, width * 0.5, valueY);
    context.shadowColor = 'transparent';
    context.strokeStyle = variant.palette.paper;
    context.lineWidth = height * 0.012;
    context.strokeText(variant.denominationLabel, width * 0.5, valueY);
    const valueGradient = context.createLinearGradient(0, valueY - valueSize * 0.55, 0, valueY + valueSize * 0.55);
    valueGradient.addColorStop(0, '#ffffff');
    valueGradient.addColorStop(0.48, variant.palette.paper);
    valueGradient.addColorStop(0.52, variant.palette.foil);
    valueGradient.addColorStop(1, variant.palette.bright);
    context.fillStyle = valueGradient;
    context.fillText(variant.denominationLabel, width * 0.5, valueY);
    context.restore();

    drawFittedText(context, variant.rewardLabel, width * 0.5, height * 0.705, {
      maxWidth: width * 0.56,
      maxSize: height * 0.064,
      minSize: height * 0.04,
      fill: variant.palette.paper,
      weight: 900,
      letterSpacing: height * 0.009,
      shadow: variant.palette.deep,
    });

    const ribbonWidth = width * 0.52;
    const ribbonHeight = height * 0.1;
    roundedRectPath(
      context,
      (width - ribbonWidth) * 0.5,
      height * 0.795,
      ribbonWidth,
      ribbonHeight,
      ribbonHeight * 0.5,
    );
    const ribbon = context.createLinearGradient((width - ribbonWidth) * 0.5, 0, (width + ribbonWidth) * 0.5, 0);
    ribbon.addColorStop(0, rgba(variant.palette.deep, 0.88));
    ribbon.addColorStop(0.5, rgba(variant.palette.ink, 0.96));
    ribbon.addColorStop(1, rgba(variant.palette.deep, 0.88));
    context.fillStyle = ribbon;
    context.fill();
    drawFittedText(context, variant.strapline, width * 0.5, height * 0.845, {
      maxWidth: ribbonWidth * 0.8,
      maxSize: height * 0.048,
      minSize: height * 0.03,
      fill: variant.palette.paper,
      weight: 800,
      letterSpacing: height * 0.008,
    });

    drawCornerValue(context, width * 0.09, height * 0.5, variant.denominationLabel, variant.palette, -Math.PI * 0.5);
    drawCornerValue(context, width * 0.91, height * 0.5, variant.denominationLabel, variant.palette, Math.PI * 0.5);
  });
}

function drawBackArtwork(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  variant: BonusCardVariant,
): void {
  context.clearRect(0, 0, width, height);
  withRoundedClip(context, width, height, height * 0.105, () => {
    drawBase(context, width, height, variant.palette, true);
    drawMotif(context, width, height, variant.motif, variant.palette, variant.artSeed ^ 0x9e3779b9);

    context.save();
    context.globalAlpha = 0.3;
    context.strokeStyle = variant.palette.paper;
    context.lineWidth = height * 0.014;
    for (let offset = -width; offset < width * 2; offset += height * 0.23) {
      context.beginPath();
      context.moveTo(offset, height);
      context.lineTo(offset + height, 0);
      context.stroke();
    }
    context.restore();

    drawInnerKeyline(context, width, height, variant.palette);
    drawSeal(context, width * 0.5, height * 0.48, height * 0.245, variant.palette);

    drawFittedText(context, 'BONUS PASS', width * 0.5, height * 0.47, {
      maxWidth: width * 0.36,
      maxSize: height * 0.075,
      minSize: height * 0.045,
      fill: variant.palette.ink,
      weight: 1000,
      letterSpacing: height * 0.008,
    });
    drawFittedText(context, variant.themeName.toUpperCase(), width * 0.5, height * 0.57, {
      maxWidth: width * 0.34,
      maxSize: height * 0.048,
      minSize: height * 0.03,
      fill: variant.palette.deep,
      weight: 850,
      letterSpacing: height * 0.006,
    });

    drawFittedText(context, `VALUE ${variant.denominationLabel}`, width * 0.5, height * 0.82, {
      maxWidth: width * 0.45,
      maxSize: height * 0.06,
      minSize: height * 0.04,
      fill: variant.palette.paper,
      weight: 900,
      letterSpacing: height * 0.009,
      shadow: variant.palette.deep,
    });
    drawSpark(context, width * 0.14, height * 0.18, height * 0.055, variant.palette.foil);
    drawSpark(context, width * 0.86, height * 0.82, height * 0.055, variant.palette.foil);
  });
}

function drawBase(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  palette: BonusCardPalette,
  darker: boolean,
): void {
  const gradient = context.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, darker ? palette.deep : palette.bright);
  gradient.addColorStop(0.38, palette.base);
  gradient.addColorStop(1, palette.deep);
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);

  const glow = context.createRadialGradient(width * 0.42, height * 0.32, 0, width * 0.42, height * 0.32, width * 0.68);
  glow.addColorStop(0, rgba(palette.paper, darker ? 0.18 : 0.32));
  glow.addColorStop(0.5, rgba(palette.bright, 0.08));
  glow.addColorStop(1, rgba(palette.ink, 0.42));
  context.fillStyle = glow;
  context.fillRect(0, 0, width, height);

  const gloss = context.createLinearGradient(0, 0, width, 0);
  gloss.addColorStop(0, 'rgba(255,255,255,0)');
  gloss.addColorStop(0.42, 'rgba(255,255,255,0.16)');
  gloss.addColorStop(0.51, 'rgba(255,255,255,0.035)');
  gloss.addColorStop(0.66, 'rgba(255,255,255,0.13)');
  gloss.addColorStop(1, 'rgba(255,255,255,0)');
  context.fillStyle = gloss;
  context.fillRect(0, 0, width, height);
}

function drawMotif(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  motif: BonusCardMotif,
  palette: BonusCardPalette,
  seed: number,
): void {
  const random = createRandom(seed);
  context.save();
  context.globalCompositeOperation = 'screen';

  switch (motif) {
    case 'sunburst':
      drawSunburst(context, width, height, palette, random);
      break;
    case 'starfield':
      drawStarfield(context, width, height, palette, random);
      break;
    case 'tide':
      drawTide(context, width, height, palette, random);
      break;
    case 'facets':
      drawFacets(context, width, height, palette, random);
      break;
    case 'orbit':
      drawOrbit(context, width, height, palette, random);
      break;
  }

  context.restore();
}

function drawSunburst(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  palette: BonusCardPalette,
  random: () => number,
): void {
  const centerX = width * 0.5;
  const centerY = height * 0.5;
  const radius = Math.hypot(width, height);
  for (let index = 0; index < 28; index += 1) {
    const angle = (index / 28) * Math.PI * 2;
    const next = angle + Math.PI / 36 + random() * Math.PI / 90;
    context.beginPath();
    context.moveTo(centerX, centerY);
    context.arc(centerX, centerY, radius, angle, next);
    context.closePath();
    context.fillStyle = rgba(index % 2 === 0 ? palette.paper : palette.bright, 0.11 + random() * 0.1);
    context.fill();
  }
}

function drawStarfield(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  palette: BonusCardPalette,
  random: () => number,
): void {
  for (let index = 0; index < 52; index += 1) {
    const x = random() * width;
    const y = random() * height;
    const radius = height * (0.008 + random() * 0.028);
    drawSpark(context, x, y, radius, random() > 0.35 ? palette.paper : palette.foil, 0.18 + random() * 0.36);
  }
}

function drawTide(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  palette: BonusCardPalette,
  random: () => number,
): void {
  context.lineCap = 'round';
  for (let index = -2; index < 10; index += 1) {
    const baseY = index * height * 0.13;
    context.beginPath();
    context.moveTo(-width * 0.1, baseY);
    for (let step = 0; step <= 8; step += 1) {
      const x = (step / 8) * width * 1.2 - width * 0.1;
      const y = baseY + Math.sin(step * 1.45 + index) * height * (0.05 + random() * 0.015);
      context.lineTo(x, y);
    }
    context.strokeStyle = rgba(index % 2 === 0 ? palette.paper : palette.bright, 0.12 + random() * 0.12);
    context.lineWidth = height * (0.018 + random() * 0.016);
    context.stroke();
  }
}

function drawFacets(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  palette: BonusCardPalette,
  random: () => number,
): void {
  const columns = 7;
  const rows = 5;
  const cellWidth = width / columns;
  const cellHeight = height / rows;
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const x = column * cellWidth;
      const y = row * cellHeight;
      context.beginPath();
      context.moveTo(x, y);
      context.lineTo(x + cellWidth, y + cellHeight * random());
      context.lineTo(x + cellWidth * random(), y + cellHeight);
      context.closePath();
      context.fillStyle = rgba(random() > 0.4 ? palette.paper : palette.bright, 0.06 + random() * 0.16);
      context.fill();
    }
  }
}

function drawOrbit(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  palette: BonusCardPalette,
  random: () => number,
): void {
  const centerX = width * 0.5;
  const centerY = height * 0.48;
  for (let index = 0; index < 9; index += 1) {
    context.save();
    context.translate(centerX, centerY);
    context.rotate(random() * Math.PI);
    context.beginPath();
    context.ellipse(0, 0, width * (0.14 + index * 0.045), height * (0.08 + index * 0.022), 0, 0, Math.PI * 2);
    context.strokeStyle = rgba(index % 2 === 0 ? palette.foil : palette.paper, 0.08 + random() * 0.16);
    context.lineWidth = height * (0.005 + random() * 0.009);
    context.stroke();
    context.restore();
  }
}

function drawPrintedFoil(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  palette: BonusCardPalette,
): void {
  const band = context.createLinearGradient(width * 0.15, 0, width * 0.85, 0);
  band.addColorStop(0, rgba(palette.foil, 0));
  band.addColorStop(0.32, rgba('#ffffff', 0.18));
  band.addColorStop(0.5, rgba(palette.foil, 0.34));
  band.addColorStop(0.68, rgba('#ffffff', 0.12));
  band.addColorStop(1, rgba(palette.foil, 0));
  context.save();
  context.translate(width * 0.52, height * 0.5);
  context.rotate(-0.18);
  context.fillStyle = band;
  context.fillRect(-width * 0.75, -height * 0.08, width * 1.5, height * 0.16);
  context.restore();
}

function drawInnerKeyline(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  palette: BonusCardPalette,
): void {
  const inset = height * 0.047;
  roundedRectPath(context, inset, inset, width - inset * 2, height - inset * 2, height * 0.073);
  context.strokeStyle = rgba(palette.paper, 0.48);
  context.lineWidth = Math.max(2, height * 0.006);
  context.stroke();

  const secondInset = inset + height * 0.019;
  roundedRectPath(
    context,
    secondInset,
    secondInset,
    width - secondInset * 2,
    height - secondInset * 2,
    height * 0.06,
  );
  context.strokeStyle = rgba(palette.deep, 0.48);
  context.lineWidth = Math.max(1, height * 0.0035);
  context.stroke();
}

function drawSeal(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  palette: BonusCardPalette,
): void {
  context.save();
  context.translate(x, y);
  const seal = context.createRadialGradient(-radius * 0.3, -radius * 0.35, radius * 0.05, 0, 0, radius);
  seal.addColorStop(0, '#ffffff');
  seal.addColorStop(0.35, palette.paper);
  seal.addColorStop(0.72, palette.foil);
  seal.addColorStop(1, palette.bright);
  context.fillStyle = seal;
  context.beginPath();
  context.arc(0, 0, radius, 0, Math.PI * 2);
  context.fill();
  context.strokeStyle = rgba(palette.deep, 0.85);
  context.lineWidth = radius * 0.12;
  context.stroke();
  context.setLineDash([radius * 0.12, radius * 0.07]);
  context.strokeStyle = rgba(palette.ink, 0.6);
  context.lineWidth = radius * 0.025;
  context.beginPath();
  context.arc(0, 0, radius * 0.79, 0, Math.PI * 2);
  context.stroke();
  context.restore();
}

function drawCornerValue(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  label: string,
  palette: BonusCardPalette,
  rotation: number,
): void {
  context.save();
  context.translate(x, y);
  context.rotate(rotation);
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.font = `900 ${Math.min(42, 150 / label.length)}px ${FONT_STACK}`;
  context.fillStyle = rgba(palette.paper, 0.9);
  context.strokeStyle = rgba(palette.deep, 0.82);
  context.lineWidth = 8;
  context.lineJoin = 'round';
  context.strokeText(label, 0, 0);
  context.fillText(label, 0, 0);
  context.restore();
}

type FittedTextOptions = Readonly<{
  maxWidth: number;
  maxSize: number;
  minSize: number;
  fill: string;
  weight: number;
  letterSpacing?: number;
  shadow?: string;
}>;

function drawFittedText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  options: FittedTextOptions,
): void {
  context.save();
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  let size = options.maxSize;
  const spacing = options.letterSpacing ?? 0;
  while (size > options.minSize) {
    context.font = `${options.weight} ${size}px ${FONT_STACK}`;
    const measured = context.measureText(text).width + Math.max(0, text.length - 1) * spacing;
    if (measured <= options.maxWidth) break;
    size -= 2;
  }
  context.font = `${options.weight} ${size}px ${FONT_STACK}`;
  context.fillStyle = options.fill;
  if (options.shadow) {
    context.shadowColor = rgba(options.shadow, 0.72);
    context.shadowBlur = size * 0.16;
    context.shadowOffsetY = size * 0.12;
  }
  if (spacing > 0) drawSpacedText(context, text, x, y, spacing);
  else context.fillText(text, x, y);
  context.restore();
}

function drawSpacedText(
  context: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  y: number,
  spacing: number,
): void {
  const glyphWidths = [...text].map((glyph) => context.measureText(glyph).width);
  const totalWidth = glyphWidths.reduce((sum, width) => sum + width, 0) + spacing * Math.max(0, glyphWidths.length - 1);
  let x = centerX - totalWidth * 0.5;
  context.textAlign = 'left';
  [...text].forEach((glyph, index) => {
    context.fillText(glyph, x, y);
    x += glyphWidths[index] + spacing;
  });
}

function drawSpark(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  color: string,
  alpha = 0.92,
): void {
  context.save();
  context.translate(x, y);
  context.beginPath();
  const points = 16;
  for (let index = 0; index < points; index += 1) {
    const angle = -Math.PI * 0.5 + (index / points) * Math.PI * 2;
    const pointRadius = index % 2 === 0 ? radius : radius * 0.28;
    const pointX = Math.cos(angle) * pointRadius;
    const pointY = Math.sin(angle) * pointRadius;
    if (index === 0) context.moveTo(pointX, pointY);
    else context.lineTo(pointX, pointY);
  }
  context.closePath();
  context.fillStyle = rgba(color, alpha);
  context.fill();
  context.restore();
}

function withRoundedClip(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  radius: number,
  draw: () => void,
): void {
  context.save();
  roundedRectPath(context, 0, 0, width, height, radius);
  context.clip();
  draw();
  context.restore();
}

function roundedRectPath(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const safeRadius = Math.min(Math.max(0, radius), width * 0.5, height * 0.5);
  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.lineTo(x + width - safeRadius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  context.lineTo(x + width, y + height - safeRadius);
  context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  context.lineTo(x + safeRadius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  context.lineTo(x, y + safeRadius);
  context.quadraticCurveTo(x, y, x + safeRadius, y);
  context.closePath();
}

function rgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return hex;
  const red = Number.parseInt(normalized.slice(0, 2), 16);
  const green = Number.parseInt(normalized.slice(2, 4), 16);
  const blue = Number.parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function createRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}
